/* eslint-disable react-refresh/only-export-components */
import { useLocation } from '@tanstack/react-router';
import React, { createContext, useContext, useEffect, useEffectEvent, useState } from 'react';

interface MenuContextType {
  isOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export function useMenuContext() {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenuContext must be used within a MenuProvider');
  }
  return context;
}

export function MenuProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const closeOnRouteChange = useEffectEvent(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    closeOnRouteChange();
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const closeMenu = () => setIsOpen(false);

  return <MenuContext.Provider value={{ isOpen, toggleMenu, closeMenu }}>{children}</MenuContext.Provider>;
}
